/**
 * Course theme context (TASK-014) — theme (Studiolo/Notturno) now lives in
 * CourseLayout (the persistent Inertia layout, see ./CourseLayout.tsx) so it
 * applies to every section, not just the reading page (pre-TASK-014
 * behaviour, when Reader.tsx alone owned `useTheme`).
 *
 * Section pages that render a `ThemePicker` (currently only Lettura.tsx,
 * inside `ReaderTopBar`) read/write the theme through this context instead
 * of owning local state — CourseLayout is the single writer of
 * `document.documentElement.dataset.theme` + localStorage.
 *
 * The default context value (not `null`) is deliberate: it lets a page
 * component render in isolation (e.g. component/unit tests) without being
 * wrapped in `<CourseLayout>` — falls back to the default theme with a
 * no-op setter rather than throwing.
 */
import { createContext, useContext } from 'react'
import type { ThemeId } from '@arianna/reader-ui'

export interface CourseThemeContextValue {
  theme: ThemeId
  setTheme: (next: ThemeId) => void
}

export const DEFAULT_COURSE_THEME: ThemeId = 'studiolo'

export const CourseThemeContext = createContext<CourseThemeContextValue>({
  theme: DEFAULT_COURSE_THEME,
  setTheme: () => {
    // No-op default — real writes only happen through CourseLayout's provider.
  },
})

export function useCourseTheme(): CourseThemeContextValue {
  return useContext(CourseThemeContext)
}
